import { PANEL_EDGE_OFFSET, applyPanelGeometry, applySide } from './chrome.js';
import { configRevalidateMs } from './constants.js';
import { fetchConfig } from './config.js';
import { resolveNudgeOptions, resolvePageQueryKeys } from './resolve-nudges.js';
import { runtime } from './runtime.js';
import { resolveOptions } from './resolve-options.js';
import { resolveTrustRhythm } from './resolve-trust.js';
import { logBubble } from './stage-log.js';

/**
 * The widget's second look at its own settings. Two ways in and one way out: the config endpoint is
 * re-read when the visitor comes back to the tab after long enough away, and the widget page
 * broadcasts a save the moment the contractor makes one. Both end in the same three appliers, which
 * is what keeps a live save and a re-read from applying the same change twice.
 *
 * Third of init()'s surfaces to become a factory. It reads more than it is read: the appliers reach
 * into the launcher, the panel and the other surfaces, but only two of them are called from outside.
 * @param {object} ctx - The instance context (see init)
 * @returns {object} The two appliers the message handler dispatches to
 */
export function createConfigRevalidation(ctx) {
    const { config, state, options, panel, button, trust, applyLiveNudges, repaintForTheme } = ctx;

    /* What the corner was drawn from, so a re-read can tell a real change from the same answer twice.
     * Sliced, because the three parts must not wake each other: re-running the bubbles would retire the
     * one on screen and re-show a dismissed one, and re-running the trust surface would put a seal back
     * in front of a visitor who is only owed it once per visit. */
    let seenConfig = JSON.stringify(config);
    state.seenNudges = JSON.stringify(resolveNudgeOptions(config));
    let seenTrust = JSON.stringify(trustSlice(config));

    /* Seeded with the read that just happened, NOT with zero: pageshow fires as the document finishes
     * loading, which can land after this listener is registered, and a zero would let it through — one
     * extra config resolve on every page view of every embedding site, for a payload seconds old. */
    let configReadAt = Date.now();

    /* Whether the page has been away since that read. Without it any focus qualifies — an alert
     * dismissed, a print dialog closed, a click back into the window after the devtools took it — and a
     * visitor who never leaves the page would pay for re-reads that cannot have anything to bring. */
    let awaySinceRead = false;

    /* One config read at a time. Only the explicit refresh below can arrive in bursts — a save button
     * clicked twice, a section that reports its change more than once — and a second fetch would race the
     * first into applyFreshConfig with no way to say which answer is the newer one. */
    let isReading = false;

    /**
     * Everything the corner shows of the trust surface, for the comparison above — the resolved forms
     * plus the pace they rotate at, which is the whole of what a save can move here.
     * @param {object|null} payload - A config (or settings-broadcast) payload
     * @returns {object}
     */
    function trustSlice(payload) {
        const source = payload || {};

        return {
            reviews: source.trustReviews || null,
            projects: source.trustProjects || null,
            awards: source.trustAwards || null,
            ownAwards: source.trustOwnAwards || null,
            topRated: source.trustTopRated || null,
            ratingSeal: source.trustRatingSeal || null,
            social: source.trustSocial || null,
            rhythm: resolveTrustRhythm(source)
        };
    }

    /**
     * Asks the server whether the settings still say what the corner is showing. The manage panel's save
     * reaches a page over the widget's own circuit — but that circuit only exists once a visitor has
     * opened the chat, and the launcher stands there long before anyone does. Without this a contractor
     * saved "Dunkel" on one screen and watched their own website keep the white seal in the corner until
     * they reloaded it.
     * Deliberately narrow, because a 304 is only cheap on the wire: the endpoint resolves the whole
     * payload — entitlement, bubbles, page questions, the trust surface — before it can compute the
     * ETag it answers with. So the ask is made only when the page is really in front of somebody, only
     * when it has been away since the last read (that is the whole of what could have changed anything
     * behind its back), and at most once per floor. A visitor who reads one page and leaves costs
     * exactly the one config request they always cost.
     */
    function revalidateConfig() {
        const now = Date.now();
        if (document.visibilityState !== 'visible'
            || !awaySinceRead
            || now - configReadAt < configRevalidateMs) {
            return;
        }

        readConfig();
    }

    /**
     * LeadWin('refresh'): the page itself says the settings just moved. Asked for rather than
     * guessed at, so none of the guards above apply — they exist to keep a visitor who never leaves the
     * page from paying for reads that cannot bring anything, and a page that KNOWS something changed is
     * the one caller that can.
     *
     * The case it exists for is the dashboard that carries a LeadWin agent of its own: the panel and the
     * launcher then sit in one document, nothing ever blurs, and the corner would keep the colour it was
     * loaded with until a reload. The circuit broadcast cannot help there — it only exists once somebody
     * has opened the chat.
     *
     * Every other page is free to call it too (a headless CMS that just published, an SPA that switched
     * tenant). One read is in flight at a time; beyond that it is the same path a refocus takes.
     */
    function refreshConfig() {
        if (isReading) {
            return;
        }

        readConfig();
    }

    /** The read itself, shared by the two entry points above. */
    function readConfig() {
        isReading = true;
        configReadAt = Date.now();
        awaySinceRead = false;

        fetchConfig().then(function (fresh) {
            isReading = false;

            // Unreachable, timed out or answered by the wrong host — whatever stands keeps standing.
            if (!fresh) {
                return;
            }

            const fingerprint = JSON.stringify(fresh);
            if (fingerprint === seenConfig) {
                logBubble('settings read again, unchanged');
                return;
            }

            logBubble('settings read again, changed');

            seenConfig = fingerprint;
            applyFreshConfig(fresh);
        }, function () {
            isReading = false;
        });
    }

    /**
     * Takes a re-read config to the corner, part by part, and only where that part really moved.
     * @param {object} fresh - The payload the revalidation brought back
     */
    function applyFreshConfig(fresh) {
        const next = resolveOptions(fresh);

        // Ahead of everything else: what counts as a page is what the bubble half below is matched by, and
        // a provider who just named a query parameter is watching their own site for exactly this.
        runtime.pageQueryKeys = resolvePageQueryKeys(fresh);

        options.accent = next.accent;
        options.contrast = next.contrast;
        options.accentDark = next.accentDark;
        options.contrastDark = next.contrastDark;
        options.tint = next.tint;
        options.gradient = next.gradient;
        options.gradientDark = next.gradientDark;
        options.theme = next.theme;

        if (next.side !== options.side) {
            applyLiveSide(next.side);
        }

        if (JSON.stringify(trustSlice(fresh)) !== seenTrust) {
            applyLiveTrust(fresh);
        }

        if (JSON.stringify(resolveNudgeOptions(fresh)) !== state.seenNudges) {
            applyLiveNudges(fresh);
        }

        repaintForTheme(); // Last: applyLiveTrust builds a fresh corner that has to wear the new look.
    }

    /**
     * Moves everything the loader pinned to a corner over to the other one. Two callers reach it — the
     * save broadcast and a re-read config — and the whole point is that they land in the same place.
     * @param {string} side - 'left' | 'right'
     */
    function applyLiveSide(side) {
        applySide(button, side, PANEL_EDGE_OFFSET);

        // The stack and the panel both anchor on the launcher, so the new side has to be on record before
        // either of them is rebuilt.
        options.side = side;

        // The panel goes through its geometry rather than through applySide: on a phone it has no side to
        // stand on — it IS the screen — and writing a left or right onto it there would pull one of its
        // edges off the window.
        applyPanelGeometry(panel, options, state.panelExpanded === true);

        // The corner is what carries the placement; the board sits inside it. Its own alignment follows
        // too, because the close control hangs off whichever edge faces the page.
        if (state.trustCardsCorner) {
            // The trust cards stand ABOVE the launcher, not beside it, so they take its own offset.
            applySide(state.trustCardsCorner, side, PANEL_EDGE_OFFSET);
            state.trustCardsCorner.style.alignItems = side === 'left' ? 'flex-start' : 'flex-end';
        }

        if (state.nudgeStack) {
            applySide(state.nudgeStack, side, PANEL_EDGE_OFFSET);
            state.nudgeStack.setAttribute('data-side', side);
            state.nudgeStack.style.alignItems = side === 'left' ? 'flex-start' : 'flex-end';
        }
    }

    /* Leaving marks the page; coming back asks. Two ways out, because between them they cover how a
     * contractor actually looks at their own website: switching tabs (the page goes hidden) and
     * clicking over to a window that sat beside the dashboard all along (side by side nothing ever
     * becomes hidden, so only blur fires). Coming back is the mirror of each, plus pageshow for the
     * back button, whose bfcache restore runs no script of its own. */
    window.addEventListener('blur', markAway);
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'visible') {
            revalidateConfig();
        } else {
            markAway();
        }
    });
    window.addEventListener('focus', revalidateConfig);
    window.addEventListener('pageshow', function (event) {
        // A bfcache restore is a page that was gone entirely; a plain load is the one we just read for.
        if (event.persisted) {
            markAway();
        }

        revalidateConfig();
    });

    /** Notes that the page lost the visitor, so the next return is allowed to ask again. */
    function markAway() {
        awaySinceRead = true;
    }

    /**
     * The contractor saved the trust settings and the frame's circuit handed the resolved surface down.
     * The corner is rebuilt from it rather than patched: which FORM stands there is part of what was
     * saved, and a pill that has to become a rotating deck is a different element, not a new text.
     * Resolved through the very services the config endpoint uses, so a live change and the next page
     * load can never disagree about what this visitor is shown.
     * @param {object} data - Message payload carrying the config's own trust fields
     */
    function applyLiveTrust(data) {
        // Both paths pass through here, so this is where the corner's fingerprint is kept current: a
        // re-read config must not rebuild (and re-show) a surface the broadcast has already applied.
        seenTrust = JSON.stringify(trustSlice(data));

        trust.rebuild(data);

        // The fresh elements are built from the widget's own setting; a host override taken through the
        // JS API outranks it and has to be put back on them.
        repaintForTheme();
    }

    return { applyLiveSide, applyLiveTrust, refreshConfig };
}
